/**
 * utils/validations/contact_validators.js
 * Real-time validators for phone, fax, email, postal code, and passport number.
 */

export const COUNTRY_CODES = [
  { code: '+27', country: 'South Africa', flag: '🇿🇦', digits: 9 },
  { code: '+1', country: 'United States', flag: '🇺🇸', digits: 10 },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧', digits: 10 },
  { code: '+61', country: 'Australia', flag: '🇦🇺', digits: 9 },
  { code: '+91', country: 'India', flag: '🇮🇳', digits: 10 },
  { code: '+234', country: 'Nigeria', flag: '🇳🇬', digits: 10 },
  { code: '+254', country: 'Kenya', flag: '🇰🇪', digits: 9 },
  { code: '+49', country: 'Germany', flag: '🇩🇪', digits: 11 },
  { code: '+33', country: 'France', flag: '🇫🇷', digits: 9 },
  { code: '+86', country: 'China', flag: '🇨🇳', digits: 11 },
  { code: '+81', country: 'Japan', flag: '🇯🇵', digits: 10 },
  { code: '+55', country: 'Brazil', flag: '🇧🇷', digits: 11 },
  { code: '+971', country: 'UAE', flag: '🇦🇪', digits: 9 },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', digits: 9 },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾', digits: 10 },
  { code: '+263', country: 'Zimbabwe', flag: '🇿🇼', digits: 9 },
  { code: '+267', country: 'Botswana', flag: '🇧🇼', digits: 7 },
  { code: '+258', country: 'Mozambique', flag: '🇲🇿', digits: 9 },
  { code: '+256', country: 'Uganda', flag: '🇺🇬', digits: 9 },
  { code: '+255', country: 'Tanzania', flag: '🇹🇿', digits: 9 },
];

function get_country_digit_limit(country_code) {
  return COUNTRY_CODES.find((item) => item.code === country_code)?.digits ?? 15;
}

function normalize_sa_contact_number(value, field_name = 'Phone number') {
  if (!value || typeof value !== 'string') {
    return { valid: false, error: `${field_name} is required` };
  }

  const cleaned = value.replace(/[\s\-()]/g, '');
  let normalised = cleaned;

  if (normalised.startsWith('+27')) {
    normalised = `0${normalised.slice(3)}`;
  } else if (normalised.startsWith('27') && normalised.length === 11) {
    normalised = `0${normalised.slice(2)}`;
  }

  if (!/^\d+$/.test(normalised)) {
    return { valid: false, error: `${field_name} must contain only digits` };
  }

  if (normalised.length !== 10) {
    return { valid: false, error: `${field_name} must be 10 digits (e.g. 0XX XXX XXXX)` };
  }

  if (!normalised.startsWith('0')) {
    return { valid: false, error: `${field_name} must start with 0 (or +27)` };
  }

  return { valid: true, error: null, formatted: normalised };
}

function validate_sa_prefix(normalised, valid_prefixes, invalid_prefix_error) {
  const prefix = normalised.slice(0, 2);
  if (!valid_prefixes.includes(prefix)) {
    return { valid: false, error: invalid_prefix_error };
  }

  return { valid: true, error: null, formatted: normalised };
}

function split_email_parts(value) {
  const at_index = value.indexOf('@');
  if (at_index <= 0 || at_index !== value.lastIndexOf('@') || at_index === value.length - 1) {
    return null;
  }

  return {
    local_part: value.slice(0, at_index),
    domain_part: value.slice(at_index + 1),
  };
}

const EMAIL_LOCAL_PART_ALLOWED = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]+$/;

function is_valid_email_local_part(local_part) {
  return Boolean(local_part)
    && local_part.length <= 64
    && !local_part.startsWith('.')
    && !local_part.endsWith('.')
    && !local_part.includes('..')
    && EMAIL_LOCAL_PART_ALLOWED.test(local_part);
}

function is_valid_email_domain_label(label) {
  if (!label || label.length > 63) return false;
  if (label.startsWith('-') || label.endsWith('-')) return false;
  return /^[A-Za-z0-9-]+$/.test(label);
}

function is_valid_email_domain_part(domain_part) {
  if (!domain_part || domain_part.length > 253) return false;
  if (domain_part.includes('..')) return false;

  const labels = domain_part.split('.');
  if (labels.length < 2) return false;

  const tld = labels[labels.length - 1];
  if (!/^[A-Za-z]{2,}$/.test(tld)) return false;

  return labels.every(is_valid_email_domain_label);
}

export function format_phone_number(digits, country_code) {
  if (!digits) return '';

  const clean = digits.replace(/\D/g, '');
  if (country_code === '+27' && clean.length <= 9) {
    if (clean.length <= 2) return clean;
    if (clean.length <= 5) return `${clean.slice(0, 2)} ${clean.slice(2)}`;
    return `${clean.slice(0, 2)} ${clean.slice(2, 5)} ${clean.slice(5)}`;
  }

  return clean.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
}

export function parse_single_field_phone_input(raw_value, fallback_code = '+27') {
  const raw = String(raw_value || '');
  const compact = raw.replace(/[^\d+]/g, '');
  const sorted_codes = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);

  let country_code = fallback_code;
  let digits = compact.replace(/\D/g, '');

  if (compact.startsWith('+')) {
    const matched_country = sorted_codes.find((item) => compact.startsWith(item.code));
    if (matched_country) {
      country_code = matched_country.code;
      digits = compact.slice(matched_country.code.length).replace(/\D/g, '');
    } else {
      digits = compact.slice(1).replace(/\D/g, '');
    }
  }

  if (country_code === '+27' && digits.length > 9 && digits.startsWith('0')) {
    digits = digits.slice(1);
  }

  const limited_digits = digits.slice(0, get_country_digit_limit(country_code));
  if (!raw.trim()) {
    return {
      country_code,
      digits: '',
      formatted: '',
    };
  }

  const formatted_body = format_phone_number(limited_digits, country_code);
  return {
    country_code,
    digits: limited_digits,
    formatted: limited_digits ? `${country_code} ${formatted_body}` : country_code,
  };
}

export function validate_phone(digits, country_code) {
  if (!digits || typeof digits !== 'string') {
    return { valid: false, error: 'Phone number is required' };
  }

  const cleaned = digits.replace(/\D/g, '');
  if (!cleaned.length) {
    return { valid: false, error: 'Phone number is required' };
  }

  const expected = COUNTRY_CODES.find((item) => item.code === country_code)?.digits ?? null;
  if (expected && cleaned.length !== expected) {
    return { valid: false, error: `Phone number must be ${expected} digits for ${country_code}` };
  }

  if (!expected && (cleaned.length < 7 || cleaned.length > 15)) {
    return { valid: false, error: 'Phone number must be between 7 and 15 digits' };
  }

  return {
    valid: true,
    error: null,
    formatted: `${country_code} ${format_phone_number(cleaned, country_code)}`,
  };
}

export function validate_sa_phone_number(value) {
  const normalized = normalize_sa_contact_number(value, 'Phone number');
  if (!normalized.valid) return normalized;

  return validate_sa_prefix(
    normalized.formatted,
    ['06', '07', '08'],
    'Not a valid SA mobile prefix (must start with 06, 07 or 08)'
  );
}

export function validate_telephone(value) {
  const normalized = normalize_sa_contact_number(value, 'Telephone number');
  if (!normalized.valid) return normalized;

  return validate_sa_prefix(
    normalized.formatted,
    ['01', '02', '03', '04', '05', '06', '07', '08'],
    'Not a valid SA telephone prefix'
  );
}

export function validate_fax(value) {
  return normalize_sa_contact_number(value, 'Fax number');
}

export function validate_email(value) {
  if (!value || typeof value !== 'string') {
    return { valid: false, error: 'Email address is required' };
  }

  const trimmed = value.trim();
  if (!trimmed.length) {
    return { valid: false, error: 'Email address is required' };
  }

  if (trimmed.length > 254) {
    return { valid: false, error: 'Email address is too long' };
  }

  const parts = split_email_parts(trimmed);
  if (!parts) {
    return { valid: false, error: 'Please enter a valid email address (e.g. name@example.com)' };
  }

  if (!is_valid_email_local_part(parts.local_part) || !is_valid_email_domain_part(parts.domain_part)) {
    return { valid: false, error: 'Please enter a valid email address (e.g. name@example.com)' };
  }

  return { valid: true, error: null, formatted: trimmed.toLowerCase() };
}

export function validate_postal_code(value) {
  if (!value || typeof value !== 'string') {
    return { valid: false, error: 'Postal code is required' };
  }

  const cleaned = value.replace(/\s/g, '');
  if (!/^\d+$/.test(cleaned)) {
    return { valid: false, error: 'Postal code must contain only digits' };
  }

  if (cleaned.length !== 4) {
    return { valid: false, error: 'SA postal code must be exactly 4 digits' };
  }

  const numeric = parseInt(cleaned, 10);
  if (numeric < 1 || numeric > 9999) {
    return { valid: false, error: 'Postal code must be between 0001 and 9999' };
  }

  return { valid: true, error: null, formatted: cleaned };
}

export function validate_passport_number(value) {
  if (!value || typeof value !== 'string') {
    return { valid: false, error: 'Passport number is required' };
  }

  const cleaned = value.replace(/[\s-]/g, '').toUpperCase();
  if (!cleaned.length) {
    return { valid: false, error: 'Passport number is required' };
  }

  if (!/^[A-Z0-9]+$/.test(cleaned)) {
    return { valid: false, error: 'Passport number must contain only letters and digits' };
  }

  if (cleaned.length < 6 || cleaned.length > 9) {
    return { valid: false, error: 'Passport number must be between 6 and 9 characters' };
  }

  return { valid: true, error: null, formatted: cleaned };
}

export function validate_contact_field(field_type, value) {
  const validators = {
    sa_phone_number: validate_sa_phone_number,
    telephone: validate_telephone,
    phone: (raw_value) => {
      const parsed = parse_single_field_phone_input(raw_value);
      return validate_phone(parsed.digits, parsed.country_code);
    },
    fax: validate_fax,
    email: validate_email,
    postal_code: validate_postal_code,
    passport_number: validate_passport_number,
  };

  const validator = Reflect.get(validators, field_type);
  if (!validator) {
    return { valid: true, error: null };
  }

  return validator(value);
}

export const CONTACT_FIELD_TYPES = new Set([
  'sa_phone_number',
  'telephone',
  'phone',
  'fax',
  'email',
  'postal_code',
  'passport_number',
]);
